import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { hexAlpha } from '../../lib/mapViewHelpers';
import ColorScaleLegend from '../ColorScaleLegend';

export default function MapOverlays({ tool, setShowAnalysisPanel, setAnalysisInit, currentProject, usgsData, importedLayers = [], resultLayers, projectDatasetCount = 0, xyzStats, setLayerActiveZ, activeXyzLayer, measurePoints, measureDone, measureTotalKm, resetMeasure, xyzHover }) {
  const { C } = useTheme();
  return (
    <>
      {currentProject && !currentProject.location?.toLowerCase().includes('cape cod') && Object.keys(usgsData).length === 0 && importedLayers.length === 0 && resultLayers.length === 0 && !(projectDatasetCount > 0) && (
        <div style={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          zIndex: 10, textAlign: 'center', padding: '24px 32px',
          background: hexAlpha(C.bg0, 0.9), border: `1px solid ${C.accentBorder}`,
          borderRadius: 12, backdropFilter: 'blur(8px)',
        }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>📡</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: C.text0, marginBottom: 6 }}>
            {currentProject.name}
          </div>
          <div style={{ fontSize: 12, color: C.text2, marginBottom: 12 }}>
            No survey data uploaded yet for this project.
          </div>
          <div style={{ fontSize: 11, color: C.text3 }}>
            Upload datasets from the project page to see data on the map.
          </div>
        </div>
      )}
      {/* XYZ hover tooltip */}
      {xyzHover && (
        <div style={{
          position: 'absolute', left: xyzHover.x + 12, top: xyzHover.y + 12, zIndex: 8,
          background: hexAlpha(C.bg0, 0.94), border: `1px solid ${C.accentBorder}`,
          borderRadius: 6, padding: '6px 9px', pointerEvents: 'none', maxWidth: 220,
        }}>
          {(xyzHover.zFields || []).map(z => (
            <div key={z} style={{ fontSize: 10, color: C.text1, display: 'flex', gap: 8, justifyContent: 'space-between' }}>
              <span style={{ color: '#fbbf24' }}>{z}</span>
              <span style={{ fontFamily: 'monospace' }}>{typeof xyzHover.props?.[z] === 'number' ? xyzHover.props[z].toLocaleString() : String(xyzHover.props?.[z] ?? '—')}</span>
            </div>
          ))}
        </div>
      )}

      {/* XYZ Z-field selector panel */}
      {activeXyzLayer && tool !== 'measure' && (
        <div style={{
          position: 'absolute', top: 10, right: 52, zIndex: 6,
          background: hexAlpha(C.bg0, 0.92), border: `1px solid ${C.accentBorder}`,
          borderRadius: 8, padding: '8px 12px', width: 195,
        }}>
          <div style={{ fontSize: 9, fontWeight: 600, color: C.accent, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            🔢 {activeXyzLayer.name}
          </div>
          <select
            value={activeXyzLayer.activeZ || ''}
            onChange={e => setLayerActiveZ(activeXyzLayer.id, e.target.value)}
            style={{ width: '100%', fontSize: 11, padding: '4px 6px', borderRadius: 6, background: C.bg4, border: `1px solid ${C.border}`, color: C.text0, outline: 'none', marginBottom: 6 }}>
            {(activeXyzLayer.xyz.zFields || []).map(z => <option key={z} value={z}>{z}</option>)}
          </select>
          {xyzStats[activeXyzLayer.id] && (
            <div style={{ fontSize: 9, color: C.text2, fontFamily: 'monospace', lineHeight: 1.6 }}>
              n: {xyzStats[activeXyzLayer.id].count.toLocaleString()}<br />
              min: {xyzStats[activeXyzLayer.id].min.toLocaleString(undefined, { maximumFractionDigits: 3 })} · max: {xyzStats[activeXyzLayer.id].max.toLocaleString(undefined, { maximumFractionDigits: 3 })}<br />
              mean: {xyzStats[activeXyzLayer.id].mean.toLocaleString(undefined, { maximumFractionDigits: 3 })} · σ: {xyzStats[activeXyzLayer.id].std.toLocaleString(undefined, { maximumFractionDigits: 3 })}
            </div>
          )}
          <button
            onClick={() => {
              setAnalysisInit({
                _k: Date.now(),
                tool: 'gridding',
                params: { sourceLayer: activeXyzLayer.id, valueAttr: activeXyzLayer.activeZ, gridRes: '60', method: 'idw' },
              });
              setShowAnalysisPanel(true);
            }}
            style={{ width: '100%', marginTop: 6, fontSize: 11, padding: '5px', borderRadius: 6, background: 'linear-gradient(135deg,#00b8de,#3b82f6)', color: '#fff', border: 'none', fontWeight: 600, cursor: 'pointer' }}>
            ▦ Grid Data
          </button>
        </div>
      )}

      {/* Color scale legend — latest visible grid/contour result */}
      {(() => {
        const legendLayer = [...resultLayers].reverse().find(r =>
          r.visible && (r.kind === 'grid' || r.kind === 'contour') && r.geojson?.metadata);
        if (!legendLayer) return null;
        const md = legendLayer.geojson.metadata;
        return (
          <ColorScaleLegend
            title={legendLayer.name}
            min={md.min}
            max={md.max}
            colors={md.colors || ['#2563eb', '#00b8de', '#34d399', '#fbbf24', '#f87171']}
          />
        );
      })()}

      {/* Measurement readout overlay */}
      {tool === 'measure' && (
        <div style={{
          position: 'absolute', top: 10, right: 52, zIndex: 6,
          background: hexAlpha(C.bg0, 0.9), border: '1px solid rgba(251,191,36,0.35)',
          borderRadius: 8, padding: '8px 12px', minWidth: 170,
        }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: '#fbbf24', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            📏 Measure Distance
          </div>
          {measurePoints.length < 2 ? (
            <div style={{ fontSize: 10, color: C.text2 }}>Click points on the map.<br />Double-click to finish.</div>
          ) : (
            <>
              <div style={{ fontSize: 16, fontWeight: 700, color: C.text0 }}>
                {measureTotalKm >= 1 ? `${measureTotalKm.toFixed(2)} km` : `${(measureTotalKm * 1000).toFixed(1)} m`}
              </div>
              <div style={{ fontSize: 10, color: C.text2 }}>
                {measurePoints.length} points · {(measureTotalKm * 0.621371).toFixed(2)} mi
                {measureDone ? ' · done' : ''}
              </div>
            </>
          )}
          {measurePoints.length > 0 && (
            <button onClick={() => resetMeasure()}
              style={{ marginTop: 6, fontSize: 10, padding: '3px 8px', borderRadius: 6, background: 'rgba(251,191,36,0.12)', color: '#fbbf24', border: '1px solid rgba(251,191,36,0.25)', cursor: 'pointer' }}>
              Clear
            </button>
          )}
        </div>
      )}
    </>
  );
}
